// Valid values for component props
const VALID_VARIANTS = ["default", "spacious"]
const VALID_PHOTO_OVERLAY_DIRECTIONS = [
  "bottom-top",
  "top-bottom",
  "left-right",
  "right-left",
  "tint",
]
const VALID_PHOTO_OVERLAY_OPACITIES = [10, 15, 20, 25, 30, 35, 40, 45]

/**
 * Validates TwoUpFeaturedCards component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateTwoUpFeaturedCardsProps = (
  props,
  throwOnError = false
) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(
        `TwoUpFeaturedCards validation failed:\n${errors.join("\n")}`
      )
    }
    return { isValid: false, errors }
  }

  // Validate required cards
  if (!props.cards) {
    errors.push("cards is required")
  } else if (!Array.isArray(props.cards)) {
    errors.push("cards must be an array")
  } else if (props.cards.length === 0) {
    errors.push("cards array must not be empty")
  } else {
    props.cards.forEach((card, index) => {
      if (typeof card !== "object" || card === null) {
        errors.push(`cards[${index}] must be an object`)
      } else {
        // Validate required CTA
        if (!card.CTA) {
          errors.push(`cards[${index}].CTA is required`)
        } else {
          if (typeof card.CTA !== "object") {
            errors.push(`cards[${index}].CTA must be an object`)
          } else {
            if (!card.CTA.text) {
              errors.push(`cards[${index}].CTA.text is required`)
            }
            if (!card.CTA.href && !card.CTA.onClick) {
              errors.push(
                `cards[${index}].CTA must have either href or onClick`
              )
            }
          }
        }

        // Validate required image
        if (!card.image) {
          errors.push(`cards[${index}].image is required`)
        } else {
          if (typeof card.image !== "object") {
            errors.push(`cards[${index}].image must be an object`)
          } else {
            if (!card.image.src) {
              errors.push(`cards[${index}].image.src is required`)
            }
            if (!card.image.alt) {
              errors.push(`cards[${index}].image.alt is required`)
            }
          }
        }

        // Validate optional eyebrow
        if (card.eyebrow !== undefined) {
          if (typeof card.eyebrow !== "object" || card.eyebrow === null) {
            errors.push(`cards[${index}].eyebrow must be an object if provided`)
          } else {
            if (!card.eyebrow.text) {
              errors.push(
                `cards[${index}].eyebrow.text is required when eyebrow is provided`
              )
            }
          }
        }

        // Validate optional title
        if (card.title !== undefined) {
          if (typeof card.title !== "object" || card.title === null) {
            errors.push(`cards[${index}].title must be an object if provided`)
          } else {
            if (!card.title.text) {
              errors.push(
                `cards[${index}].title.text is required when title is provided`
              )
            }
          }
        }

        // Validate optional opacity
        if (card.opacity !== undefined) {
          if (!VALID_PHOTO_OVERLAY_OPACITIES.includes(card.opacity)) {
            errors.push(
              `cards[${index}].opacity must be one of: ${VALID_PHOTO_OVERLAY_OPACITIES.join(
                ", "
              )}`
            )
          }
        }

        // Validate optional gradientDirection
        if (card.gradientDirection !== undefined) {
          if (
            !VALID_PHOTO_OVERLAY_DIRECTIONS.includes(card.gradientDirection)
          ) {
            errors.push(
              `cards[${index}].gradientDirection must be one of: ${VALID_PHOTO_OVERLAY_DIRECTIONS.join(
                ", "
              )}`
            )
          }
        }

        // Validate optional showCTA
        if (card.showCTA !== undefined) {
          if (typeof card.showCTA !== "boolean") {
            errors.push(`cards[${index}].showCTA must be a boolean if provided`)
          }
        }
      }
    })
  }

  // Validate optional variant
  if (props.variant !== undefined) {
    if (!VALID_VARIANTS.includes(props.variant)) {
      errors.push(`variant must be one of: ${VALID_VARIANTS.join(", ")}`)
    }
  }

  // Validate optional topSpacer
  if (props.topSpacer !== undefined) {
    if (typeof props.topSpacer !== "object" || props.topSpacer === null) {
      errors.push("topSpacer must be an object if provided")
    }
  }

  // Validate optional bottomSpacer
  if (props.bottomSpacer !== undefined) {
    if (typeof props.bottomSpacer !== "object" || props.bottomSpacer === null) {
      errors.push("bottomSpacer must be an object if provided")
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(
      `TwoUpFeaturedCards validation failed:\n${errors.join("\n")}`
    )
  }

  return { isValid, errors }
}
